// Single source of truth for ESG framework display metadata. Keyed by
// canonical code. Shapes the three historical maps (frameworkInfos,
// FRAMEWORK_METADATA, getFrameworkColor) derive their data from.
//
// Codes are split into three buckets:
//   - canonical ESG_FRAMEWORKS: the 7 disclosure-bearing frameworks
//   - synthetic UI-only: UNIVERSAL (CUSTOM is rendered separately because
//     it has no static metadata; it derives at runtime)
//   - displayOnly: SASB, SET, ASX — shown in the report-type picker as
//     disabled, never sent to the API

const META = {
  UNIVERSAL: {
    label: "Universal ESG Report",
    image: "/images/esg/universal.svg",
    summary:
      "Create a single report covering all supported frameworks at once.",
    usage:
      "Use when you need to report across multiple frameworks simultaneously.",
    points: [
      "Covers disclosures from all supported frameworks",
      "Fill shared metrics once — automatically maps to each framework",
      "Export as universal or per-framework report",
    ],
    synthetic: true,
  },
  "JJ-Group": {
    label: "Jebsen&Jessen Group",
    shortName: "JJ-Group ESG Framework",
    description:
      "Cross-cutting ESG disclosures that apply broadly across industries and geographies.",
    summary:
      "Cross-cutting ESG disclosures that apply broadly across industries and geographies.",
    usage:
      "Use when you need a common baseline set of environment, social, and governance metrics.",
    points: [
      "Covers widely-used ESG topics like energy, emissions, people, and ethics",
      "Ideal as a starter pack before mapping to market-specific frameworks",
      "Keeps disclosures framework-agnostic for flexible reporting",
    ],
    image: "/images/esg/jjsea.png",
    color: "#7c3aed",
    badgeClass:
      "bg-transparent text-violet-700 border-violet-300 dark:text-violet-300",
  },
  SGX: {
    label: "Singapore Exchange (SGX) Sustainability Reporting",
    shortName: "Singapore Exchange ESG Reporting",
    description:
      "Listing rules and sustainability reporting guidelines aligned to international practices.",
    summary:
      "Listing rules and sustainability reporting guidelines aligned to international practices.",
    usage: "Best for SGX-listed or Singapore-based sustainability reporting.",
    points: [
      "Comply-or-explain ESG reporting approach",
      "Board accountability and assurance guidance",
      "Local alignment with global frameworks",
    ],
    image: "/images/esg/sgx.png",
    color: "#0369a1",
    badgeClass: "bg-transparent text-blue-500 border-blue-500",
  },
  BURSA: {
    label: "Bursa Malaysia",
    shortName: "Bursa Malaysia Sustainability Reporting",
    description:
      "Malaysian sustainability reporting framework with general disclosures, common indicators, and sector-specific metrics.",
    summary:
      "Malaysian sustainability reporting framework with general disclosures, common indicators, and sector-specific metrics.",
    usage: "Best for Bursa Malaysia-listed companies sustainability reporting.",
    points: [
      "Structured governance and oversight disclosures",
      "Common indicators across key ESG topics",
      "Industry-specific sustainability metrics",
    ],
    image: "/images/esg/bursa.png",
    color: "#dc2626",
    badgeClass: "bg-transparent text-purple-700 border-purple-300",
    reference: {
      link: "https://my.bursamalaysia.com/learn/bursa-sustain/explorer/sustainability-reporting-guide-and-toolkits-3rd-edition",
      text: "Bursa Malaysia Sustainability Reporting Guide",
    },
    subFrameworks: ["bursa_1", "bursa_2"],
  },
  "IFRS-1": {
    label: "ISSB · IFRS S1 General Requirements",
    shortName: "IFRS S1",
    description:
      "IFRS S1 General Requirements for Disclosure of Sustainability-related Financial Information.",
    summary:
      "General sustainability-related disclosures forming the global baseline for investors.",
    usage:
      "Best for cross-topic sustainability reporting connected to financial statements.",
    points: [
      "Enterprise-value focused sustainability information",
      "Connectivity with IFRS financial reporting",
      "Applies across industries and sustainability topics",
    ],
    image: "/images/esg/issb_ifrs_s1.png",
    color: "#0891b2",
    badgeClass:
      "bg-transparent text-cyan-700 border-cyan-300 dark:text-cyan-300",
    reference: {
      link: "https://www.ifrs.org/issued-standards/ifrs-sustainability-standards/ifrs-s1-general-requirements-for-disclosure-of-sustainability-related-financial-information/",
      text: "IFRS S1 Standard",
    },
  },
  "IFRS-2": {
    label: "ISSB · IFRS S2 Climate-related Disclosures",
    shortName: "IFRS S2",
    description:
      "IFRS S2 Climate-related Disclosures standard for climate risk and opportunity reporting.",
    summary:
      "Climate-related disclosure standard aligned with TCFD structure for risks, opportunities, and metrics.",
    usage:
      "Best for climate risk, transition plans, and emissions reporting aligned to IFRS S2.",
    points: [
      "Governance, strategy, risk management, metrics and targets for climate",
      "Scenario analysis and resilience expectations",
      "Emissions, targets, and transition planning disclosures",
    ],
    image: "/images/esg/issb_ifrs_s2.png",
    color: "#6366f1",
    badgeClass:
      "bg-transparent text-indigo-700 border-indigo-300 dark:text-indigo-300",
    reference: {
      link: "https://www.ifrs.org/issued-standards/ifrs-sustainability-standards/ifrs-s2-climate-related-disclosures/",
      text: "IFRS S2 Standard",
    },
  },
  GRI: {
    label: "Global Reporting Initiative (GRI)",
    shortName: "GRI Standards",
    description:
      "Global Reporting Initiative standards for sustainability reporting used by organizations worldwide.",
    summary:
      "Comprehensive sustainability disclosures across environmental, social and governance topics.",
    usage: "Best for organization-wide sustainability reporting across topics.",
    points: [
      "Broad ESG topics and disclosures",
      "Stakeholder-focused transparency and comparability",
      "Modular structure with topic standards",
    ],
    image: "/images/esg/gri.png",
    color: "#059669",
    badgeClass: "bg-transparent text-green-700 border-green-300",
  },
  TCFD: {
    label: "Task Force on Climate-related Financial Disclosures (TCFD)",
    shortName: "TCFD Framework",
    description:
      "Task Force on Climate-related Financial Disclosures framework for climate risk reporting.",
    summary:
      "Governance, strategy, risk management, and metrics/targets for climate reporting.",
    usage:
      "Best for climate-related risk, governance, and strategy disclosures.",
    points: [
      "Scenario analysis and risk management",
      "Board oversight and management roles",
      "Emissions metrics and target tracking",
    ],
    image: "/images/esg/tcfd.png",
    color: "#d97706",
    badgeClass: "bg-transparent text-orange-700 border-orange-300",
  },
  SASB: {
    label: "Sustainability Accounting Standards Board (SASB)",
    image: "/images/esg/sasb.png",
    summary:
      "Industry-specific metrics focused on financial materiality for investors.",
    usage: "Best for investor-focused, industry-specific metrics and KPIs.",
    points: [
      "Industry-focused KPIs and guidance",
      "Materiality-driven metrics and narratives",
      "Complements mainstream financial filings",
    ],
    disabled: true,
  },
  SET: {
    label: "SET/FTSE Russell",
    subtitle: "Thailand and FTSE ESG ratings",
    image: "/images/esg/set.png",
    disabled: true,
  },
  ASX: {
    label: "ASX",
    subtitle: "Australian Securities Exchange ESG guidelines",
    image: "/images/esg/asx.png",
    disabled: true,
  },
};

// Display order for pickers (frameworkInfos preserves this order).
const DISPLAY_ORDER = [
  "UNIVERSAL",
  "JJ-Group",
  "SGX",
  "BURSA",
  "IFRS-1",
  "IFRS-2",
  "GRI",
  "TCFD",
  "SASB",
  "SET",
  "ASX",
];

export const FRAMEWORK_METADATA = META;
export const FRAMEWORK_DISPLAY_ORDER = DISPLAY_ORDER;

export const getFrameworkMeta = (code) =>
  META[code] ?? {
    label: code,
    shortName: code,
    description: "",
    color: "#6b7280",
  };
